<template>
  <div class="autocomplete-wrapper mb-4">
    <label v-if="label" class="input-label">{{ label }}</label>
    <div
      class="input-container"
      :class="{ error: error, highlight: highlight }"
    >
      <div class="input-field-container">
        <input
          type="text"
          :value="modelValue"
          @input="($event) => onInput($event.target.value)"
          :placeholder="placeholder"
          class="input-field"
          @focus="onFocusInput"
          @blur="onBlur"
          :style="
            selectedOption && selectedOption.isCovered === 'false'
              ? 'color: #bdbdbd; padding-right: 100px;'
              : ''
          "
        />
        <span
          v-if="selectedOption && selectedOption.isCovered === 'false'"
          style="
            position: absolute;
            right: 18px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 13px;
            color: #bdbdbd;
            background: white;
            padding-left: 8px;
            pointer-events: none;
          "
        >
          {{ t("PAYJOY_CARD_VIEWS.NO_COVERAGE") }}
        </span>
      </div>
    </div>
    <div v-if="showDropdown && options.length" class="options-dropdown">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="option-item"
        @click="
          selectOption(typeof option === 'object' ? option.label : option)
        "
        :style="
          typeof option === 'object' &&
          (option.isCovered === false || option.isCovered === 'false')
            ? 'color: #bdbdbd;'
            : ''
        "
      >
        <slot name="option" :option="option">
          <span>
            {{ typeof option === "object" ? option.label : option }}
          </span>
          <span
            v-if="
              typeof option === 'object' &&
              (option.isCovered === false || option.isCovered === 'false')
            "
            style="float: right; font-size: 13px; color: #bdbdbd"
          >
            {{ t("PAYJOY_CARD_VIEWS.NO_COVERAGE") }}
          </span>
        </slot>
      </div>
    </div>
    <div v-if="error && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
function onInput(value: string) {
  emit("update:modelValue", value);
  emit("input", value);
}
import { ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

const { t } = useI18n();

const isSelecting = ref(false);

const props = defineProps<{
  modelValue: string;
  options: Array<
    string | { label: string; value: string; isCovered?: boolean }
  >;
  label?: string;
  placeholder?: string;
  error: boolean;
  errorMessage?: string;
  highlight?: boolean;
  selectedOption?: { label: string; value: string; isCovered: string } | null;
  name?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue"): void;
  (e: "onBlur"): void;
  (e: "optionSelected"): void;
  (e: "onFocus"): void;
  (e: "input"): void;
}>();

const showDropdown = ref(false);

const onFocusInput = () => {
  showDropdown.value = true;
  emit("onFocus", props.name);
};

const onBlur = (event: FocusEvent) => {
  if (isSelecting.value) {
    return;
  }

  setTimeout(() => {
    if (!isSelecting.value) {
      showDropdown.value = false;
      emit("onBlur", (event.target as HTMLInputElement).value);
    }
    isSelecting.value = false;
  }, 150);
};

const selectOption = (option: string) => {
  isSelecting.value = true;
  emit("update:modelValue", option);
  showDropdown.value = false;
  emit("optionSelected", option);

  setTimeout(() => {
    isSelecting.value = false;
  }, 200);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.autocomplete-wrapper {
  position: relative;
  width: 100%;
  z-index: 1;
}

.input-label {
  font-size: 14px;
  position: absolute;
  top: -10px;
  left: 10px;
  margin-bottom: 4px;
  color: #595a5a;
  font-family: Inter;
  background-color: white;
  z-index: 2;
}

.input-container {
  border: 1px solid $pj-color-light-gray;
  border-radius: 5px;
  padding: 5px 12px;
  background-color: white;

  &.error {
    border-color: $pj-color-red !important;
  }

  &.highlight {
    border-color: #aed3ad;
  }
}

.input-field {
  width: 100%;
  height: 40px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  font-family: Inter;
  color: #111111;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #8b8b8b;
    font-weight: 400;
    font-size: 14px;
  }
}

.options-dropdown {
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000 !important;
}

.option-item {
  padding: 12px 20px;
  cursor: pointer;

  &:hover {
    background-color: rgba($pj-color-light-gray, 0.2);
  }
}

.error-message {
  color: #d80000;
  font-size: 12px;
  font-family: Inter;
  font-weight: 400;
}

.input-field-container {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1;
}
</style>
